import { FC, ReactNode, useContext, useRef } from 'react'
import { killAllFloatingPanesActionCreator, ModifyFloatingPaneAction } from 'actions/floatingPanesActions'
import { BaseDispatchButtonProps, DispatchButton } from 'components/features/pfm/Button/DispatchButton'
import { CreditCardFloatingPane, CREDIT_CARD_PANE, PayCardPaneStep } from 'components/features/creditCard/creditCardStore/floatingPane'
import { ButtonProps } from 'components/features/pfm/Button/Button'
import { FLOATING_PANE_INVITE_FRIENDS_PANE } from 'components/features/inviteFriendsPane/constants/pane'
import { find } from 'lodash'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { cardUpcomingPaymentsSelector } from 'components/features/creditCard/creditCardStore/selectors'
import { TransactionUid } from 'interfaces/moneyflow'
import { PaneContext } from 'components/features/pfm/FloatingPaneHost/FloatingPaneHost'

export const BACK_BUTTON_PROPS: ButtonProps = {
  text: 'Back',
  theme: 'lightBlue',
  qaType: 'button',
  qaMarker: 'back',
  iconSlug: 'chevron-left',
  fullWidth: true,
}

export const CANCEL_BUTTON_PROPS: ButtonProps = {
  text: 'Cancel',
  style: 'textOnly',
  qaType: 'button',
  qaMarker: 'cancel',
  fullWidth: true,
}

export type PaneButtonProps =
  & BaseDispatchButtonProps
  & {
    backButton?: ReactNode | null
  }

export type WithUidPaneButtonProps =
  & PaneButtonProps
  & {
    uid: TransactionUid
  }

export type CreditCardPaneButtonProps =
  & PaneButtonProps
  & {
    pane: CreditCardFloatingPane
  }


/**========================================================================
 *                          GLOBAL PANES
 *========================================================================**/
export const KillAllPanesButton: FC<PaneButtonProps> = (props) => {
  const actions = [
    killAllFloatingPanesActionCreator(),
  ]
  return <DispatchButton actions={ actions } { ...props } />
}

export const InviteFriendsPaneButton: FC<PaneButtonProps> = (props) => {
  const actions = [
    new ModifyFloatingPaneAction({
      type: FLOATING_PANE_INVITE_FRIENDS_PANE,
    }),
  ]
  return <DispatchButton actions={ actions } { ...props } />
}

/**========================================================================
 *                          TRANSACTIONS
 *========================================================================**/

export const TransactionDetailsPaneButton: FC<WithUidPaneButtonProps> = ({ uid, ...otherProps }) => {
  const actions = [
    new ModifyFloatingPaneAction({
      type: 'transaction-details',
      uid,
    }),
  ]
  return <DispatchButton actions={ actions } { ...otherProps } />
}


/**========================================================================
 *                          FUTURE PLANNER PANES
 *========================================================================**/

export const MemberLayersPaneButton: FC<PaneButtonProps> = (props) => {
  const actions = [
    killAllFloatingPanesActionCreator(),
    new ModifyFloatingPaneAction({
      type: 'future',
      mode: 'member-layers',
    }),
  ]
  return <DispatchButton actions={ actions } { ...props } />
}

export const AllLayersPaneButton: FC<PaneButtonProps> = (props) => {
  const actions = [
    killAllFloatingPanesActionCreator(),
    new ModifyFloatingPaneAction({
      type: 'future',
      mode: 'all-layers',
    }),
  ]
  return <DispatchButton actions={ actions } { ...props } />
}

/**========================================================================
 *                           COACHING PANES
 *========================================================================**/

export const NewCoachingSessionPaneButton: FC<PaneButtonProps> = (props) => {
  const actions = [
    new ModifyFloatingPaneAction({
      type: 'advisor',
      mode: 'request',
      step: 'topic',
      fields: {
        flow: 'regular',
      },
    }),
  ]
  return <DispatchButton actions={ actions } { ...props } />
}

/**========================================================================
 *                           CREDIT CARD PANES
 *========================================================================**/

export const CreditCardFloatingPaneButton: FC<CreditCardPaneButtonProps> = (props) => {
  const { pane, backButton, ...otherProps } = props

  const actions = [
    new ModifyFloatingPaneAction({
      ...pane,
      type: CREDIT_CARD_PANE,
      backButton,
    }),
  ]
  return <DispatchButton actions={ actions } { ...otherProps } />
}

/* ---------------------- Credit Card - Management ---------------------- */


export const CardMenuPaneButton: FC<PaneButtonProps> = (props) => {
  const actions = [
    new ModifyFloatingPaneAction({
      mode: 'cardMenu',
      type: CREDIT_CARD_PANE,
    }),
  ]
  return <DispatchButton actions={ actions } { ...props } />
}

export const AddCardToWalletPaneButton: FC<PaneButtonProps> = (props) => (
  <CreditCardFloatingPaneButton { ...props } pane={{ mode: 'wallet' } as CreditCardFloatingPane} />
)

export const CardTermsPaneButton: FC<PaneButtonProps> = (props) => (
  <CreditCardFloatingPaneButton { ...props } backButton={ null } pane={{ mode: 'terms' } as CreditCardFloatingPane} />
)

export const CardDetailsPaneButton: FC<PaneButtonProps> = (props) => (
  <CreditCardFloatingPaneButton { ...props } pane={{ mode: 'cardDetails' } as CreditCardFloatingPane} />
)

export const BalanceDetailsPaneButton: FC<PaneButtonProps> = (props) => (
  <CreditCardFloatingPaneButton { ...props } pane={{ mode: 'balance' } as CreditCardFloatingPane} />
)

export const ChangePinPaneButton: FC<PaneButtonProps> = (props) => (
  <CreditCardFloatingPaneButton { ...props } pane={{ mode: 'changePin' } as CreditCardFloatingPane} />
)

export const CardStatementsPaneButton: FC<PaneButtonProps> = (props) => (
  <CreditCardFloatingPaneButton { ...props } pane={{ mode: 'statements' } as CreditCardFloatingPane} />
)

export const CardPaymentsPaneButton: FC<PaneButtonProps> = (props) => (
  <CreditCardFloatingPaneButton { ...props } pane={{ mode: 'payments' } as CreditCardFloatingPane} />
)

/* ----------------------- Credit Card - Lock / Replace ----------------------- */

export const ReplaceCardPaneButton: FC<PaneButtonProps> = (props) => (
  <CreditCardFloatingPaneButton
    { ...props }
    pane={{
      mode: 'replaceCard',
      step: 'reason',
    } as CreditCardFloatingPane}
  />
)

export const LockCardPaneButton: FC<PaneButtonProps> = (props) => (
  <CreditCardFloatingPaneButton
    { ...props }
    pane={{
      mode: 'lockOrUnlock',
    } as CreditCardFloatingPane}
  />
)

/* ----------------------- Credit Card - Payments Methods ----------------------- */

export const AddPaymentMethodsPaneButton: FC<PaneButtonProps> = (props) => (
  <CreditCardFloatingPaneButton
    iconSlug='plus'
    { ...props }
    pane={{
      mode: 'addPaymentMethod',
    } as CreditCardFloatingPane}
  />
)

export const PaymentMethodsPaneButton: FC<PaneButtonProps> = (props) => (
  <CreditCardFloatingPaneButton { ...props } pane={{ mode: 'paymentMethods' } as CreditCardFloatingPane} />
)

/* ----------------------- Credit Card - Pay Card ----------------------- */

type BasePayCardStepPaneButtonProps =
  & PaneButtonProps
  & {
    isAutoPay?: boolean
  }

type PayCardStepPaneButtonProps =
  & BasePayCardStepPaneButtonProps
  & {
    step: PayCardPaneStep
  }

export const PayCardStepPaneButton: FC<PayCardStepPaneButtonProps> = (props) => {
  const { isAutoPay, step, ...otherProps } = props
  const pane = useContext(PaneContext) as CreditCardFloatingPane
  const isAutoPayMode = isAutoPay || pane?.mode === 'autoPay'

  const upcomingPayments = useStoreSelector(cardUpcomingPaymentsSelector)
  const upcomingAutoPayment = upcomingPayments?.find((payment) => payment.schedule_type === 'autopay')
  const uid = upcomingAutoPayment?.uid

  return (
    <CreditCardFloatingPaneButton
      { ...otherProps }
      backButton={ props.backButton || null }
      pane={{
        mode: isAutoPayMode ? 'autoPay' : 'payCard',
        uid: isAutoPayMode ? uid : undefined,
        step,
      } as CreditCardFloatingPane}
    />
  )
}

export const PayCardAmountPaneButton: FC<BasePayCardStepPaneButtonProps> = (props) => (
  <PayCardStepPaneButton step='amount' { ...props } />
)

export const PayCardDatePaneButton: FC<BasePayCardStepPaneButtonProps> = (props) => {
  // Use ref to click this button programatically (for example onSwipe)
  const ref = useRef(null)
  return (
    <PayCardStepPaneButton
      step='date'
      backButton={ props.backButton || <PayCardAmountPaneButton { ...BACK_BUTTON_PROPS } innerRef={ ref } /> }
      { ...props }
    />
  )
}
export const PayCardMethodPaneButton: FC<BasePayCardStepPaneButtonProps> = (props) => {
  // Use ref to click this button programatically (for example onSwipe)
  const ref = useRef(null)
  return (
    <PayCardStepPaneButton
      step='paymentMethod'
      backButton={ props.backButton || <PayCardDatePaneButton { ...BACK_BUTTON_PROPS } innerRef={ ref } /> }
      { ...props }
    />
  )
}
export const PayCardConfirmPaneButton: FC<BasePayCardStepPaneButtonProps> = (props) => {
  // Use ref to click this button programatically (for example onSwipe)
  const ref = useRef(null)
  return (
    <PayCardStepPaneButton
      step='confirm'
      backButton={ props.backButton || <PayCardMethodPaneButton { ...BACK_BUTTON_PROPS } innerRef={ ref } /> }
      { ...props }
    />
  )
}

/* ----------------------- Credit Card - Auto Pay ----------------------- */

export const AutoPayPaneCTAButton: FC<PaneButtonProps> = (props) => {
  const upcomingPayments = useStoreSelector(cardUpcomingPaymentsSelector)
  const autoPayment = !!upcomingPayments ? find(upcomingPayments, ['schedule_type', 'autopay']) : undefined
  const text = (!!autoPayment ? 'Manage ' : 'Turn on ') + 'Auto-pay'
  const cancelButton = props.backButton || <KillAllPanesButton { ...CANCEL_BUTTON_PROPS } />

  return !autoPayment ? (
    <PayCardAmountPaneButton { ...props } text={ props.text || text } isAutoPay backButton={ cancelButton } />
  ) : (
    <PayCardConfirmPaneButton { ...props } text={ props.text || text } isAutoPay backButton={ cancelButton } />
  )
}
